/**
 * @desc landscape display for mobile
 * @usage 现在的场景只在course里面使用，可以自行扩展
 */
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { isInIframe, getQueryString } from '@umu/umu-dom';
import { isInUse } from '@umu/umu-util';
import { childPostControl } from '@umu/umu-iframe/src/session/postMessage/childPostControl';
import { IIframeACKParentModel, SessionMessageAction, IIframeNeedLoginACKChild } from '@umu/umu-iframe/src/session/messageActions';
import { isParentLocationCourse, isLandscapeByUrlQuery } from './helper';
import { isRegisterUser } from '../accountService';
import { reportError } from 'common/src/services/errorLogger';

const timeoutForParentLocation = 5000;

// 样式固定
const fixBodyWidth = () => {
    document.body.classList.add('landscape');
    document.body.classList.add('is-iframe-mode');
};
const fixUSEBodyStyle = () => {
    document.body.classList.add('is-in-use');
};

const isIframe = isInIframe();
if (isIframe) {
    // 需要比 is-iframe-mode 的注入时机早，防止加载时出现闪烁
    if (isInUse()) {
        fixUSEBodyStyle();
    }
    const fromTo = getQueryString('fromTo');
    // 将 fromTo 从驼峰或者下划线修改为中划线
    fromTo && document.body.classList.add(fromTo);
    fixBodyWidth();
}

export interface IWrapperUseMobileIframeLandscapeDisplayProps {
    _isIframe: boolean;
    _isDisplayInIframeModeForCourse?: boolean;
    _notifyIframeHasNavigationBar: (isUseOwn?: boolean) => void;
    _openSideIframe: (url: string, title?: string) => void;
    extraData?: {
        isRoche?: boolean;
        [key: string]: any;
    };
}

/**
 * @desc 高阶组件的给外界使用，inject iframe数据
 * @param ComponentClassFactory
 * @returns ComponentClass
 */
export const WrapperUseMobileIframeLandscapeDisplayManager = (ComponentClassFactory: any) => (props: any) => {
    const { isDisplayInIframeModeForCourse } = useMobileIframeLandscapeDisplayManager();
    const openSideIframe = useCallback(
        (url: string, title?: string) => {
            if (isDisplayInIframeModeForCourse) {
                childPostControl.sendMsg({
                    action: SessionMessageAction.openPage,
                    data: {
                        url,
                        title: title || '',
                    },
                });
            } else {
                window.location.href = url;
            }
        },
        [isDisplayInIframeModeForCourse],
    );
    const notifyIframeHasNavigationBar = useCallback(
        (isUseOwn: boolean) => {
            if (isDisplayInIframeModeForCourse) {
                childPostControl.sendMsg({
                    action: SessionMessageAction.navigationBarBehavior,
                    data: {
                        url: location.href,
                        isUseOwn: typeof isUseOwn === 'boolean' ? isUseOwn : true,
                    },
                });
            }
        },
        [isDisplayInIframeModeForCourse],
    );
    return (
        <ComponentClassFactory
            {...{
                ...props,
                _isDisplayInIframeModeForCourse: isDisplayInIframeModeForCourse,
                _openSideIframe: openSideIframe,
                _notifyIframeHasNavigationBar: notifyIframeHasNavigationBar,
                _isIframe: isIframe,
            }}
        />
    );
};
/**
 * @desc 全局存放iframe的基础数据
 */
export const iframePageGlobalStore = {
    isIframeMode: isLandscapeByUrlQuery() && isIframe,
};
console.log(`iframe iframePageGlobalStore`, iframePageGlobalStore);
childPostControl?.listenParentLocationPromise?.then((parentLocation: IIframeACKParentModel) => {
    const isShowInIframeMode: boolean = isParentLocationCourse(parentLocation);
    iframePageGlobalStore.isIframeMode = isShowInIframeMode;
    return isShowInIframeMode;
});

export const useMobileIframeLandscapeDisplayManager = () => {
    const [isConditionForIframe, setIsConditionForIframe] = useState<boolean>(iframePageGlobalStore.isIframeMode);

    // 模拟componentWillMount,父frame下发postMessage通知，告知父级页面展示规则
    useMemo(() => {
        if (!isConditionForIframe) {
            childPostControl?.listenParentLocationPromise
                ?.then((parentLocation: IIframeACKParentModel) => {
                    const isShowInIframeMode: boolean = isParentLocationCourse(parentLocation);
                    setIsConditionForIframe(isShowInIframeMode);
                })
                .catch((e) => {
                    reportError({
                        error: e,
                        componentName: 'ResponsiveDisplayCourse',
                        additionalInfo: { desc: 'iframe childPostControl listenParentLocationPromise error' },
                    });
                });
        }
    }, []);
    // 降级parentLocation的获取，保护页面在非UMU的父级展示
    useEffect(() => {
        let timerId: any;
        if (!isConditionForIframe) {
            timerId = setTimeout(() => {
                setIsConditionForIframe(false);
            }, timeoutForParentLocation);
        }
        return () => {
            timerId && clearTimeout(timerId);
        };
    }, [isConditionForIframe]);
    return {
        isDisplayInIframeModeForCourse: isConditionForIframe,
    };
};
/**
 * @returns Promise<isIframeLogin>
 */
export const tryIframeRegisterPostLoginAlwaysResolve = (): Promise<boolean> => {
    if (!isRegisterUser() && isIframe) {
        // 让父页面处理登录
        const payload: IIframeNeedLoginACKChild = {
            action: SessionMessageAction.needLogin,
            data: {
                url: location.href,
            },
        };
        childPostControl.sendMsg(payload);
        return Promise.resolve(false);
    } else {
        return Promise.resolve(true);
    }
};
